"use client";

import React from "react";
import type {
  TabConfig,
  TabContentProps,
} from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { RecurringTransaction } from "@/lib/types/recurring-transactions";

// Import recurring transaction detail config
import { getEntityDetailConfig } from "@/lib/entity-registry";

// Import recurring transaction detail tab components
import { RecurringTransactionDetailTabComponents } from "@/features/recurring-transactions/components/RecurringTransactionDetailTabComponents";

// Generic resolution by `customComponent` key (NOT a hand-maintained switch) so
// a tab declared in the detail config can never silently fall through.
const RECURRING_TXN_TAB_COMPONENTS = RecurringTransactionDetailTabComponents as Record<
  string,
  React.ComponentType<TabContentProps<RecurringTransaction>>
>;

function resolveRecurringTransactionTabComponent(
  tab: TabConfig<RecurringTransaction> | undefined
): React.ComponentType<TabContentProps<RecurringTransaction>> {
  const custom = tab?.content?.customComponent;
  if (typeof custom === "string" && RECURRING_TXN_TAB_COMPONENTS[custom]) {
    return RECURRING_TXN_TAB_COMPONENTS[custom];
  }
  return RECURRING_TXN_TAB_COMPONENTS.RecurringTransactionDetailsTab;
}

// ============================================================================
// RECURRING TRANSACTION DETAIL MODAL TABS HOOK
// ============================================================================

export interface UseRecurringTransactionDetailModalTabsReturn {
  tabs: TabConfig<RecurringTransaction>[];
  renderPrimaryArea: (activeTab: string, recurringTransaction: RecurringTransaction) => React.ReactNode;
  renderInfoPanel: (recurringTransaction: RecurringTransaction) => React.ReactNode;
}

export function useRecurringTransactionDetailModalTabs(): UseRecurringTransactionDetailModalTabsReturn {
  // Pull ALL tabs from the detail config
  const tabs = getEntityDetailConfig<RecurringTransaction>("recurring_transactions").tabs;

  // Render primary area based on active tab
  const renderPrimaryArea = React.useCallback((activeTab: string, recurringTransaction: RecurringTransaction): React.ReactNode => {
    // Get the tab config for this tab
    const tabConfig = tabs.find((t) => t.id === activeTab) || tabs[0];
    const TabComponent = resolveRecurringTransactionTabComponent(tabConfig);
    return <TabComponent item={recurringTransaction} tab={tabConfig} />;
  }, [tabs]);

  // Render info panel (stable across tabs)
  const renderInfoPanel = React.useCallback((recurringTransaction: RecurringTransaction): React.ReactNode => {
    return (
      <div className="space-y-4">
        <div className="border rounded-lg p-4 bg-white">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Transaction Information</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600">Item:</span>
              <p className="text-gray-900 font-medium">{recurringTransaction.item}</p>
            </div>
            {recurringTransaction.description && (
              <div>
                <span className="text-gray-600">Description:</span>
                <p className="text-gray-900">{recurringTransaction.description}</p>
              </div>
            )}
            <div>
              <span className="text-gray-600">Amount:</span>
              <p className="text-gray-900 font-medium">
                ${Number(recurringTransaction.amount).toFixed(2)} × {Number(recurringTransaction.quantity) || 1}
              </p>
            </div>
            {recurringTransaction.sync_status && (
              <div>
                <span className="text-gray-600">Sync Status:</span>
                <p className="text-gray-900 font-medium capitalize">{recurringTransaction.sync_status}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, []);

  return React.useMemo(
    () => ({
      tabs,
      renderPrimaryArea,
      renderInfoPanel,
    }),
    [tabs, renderPrimaryArea, renderInfoPanel],
  );
}
